import { useState } from 'react';

/**
 * <DishImage> — simple, universally-compatible image with graceful fallback.
 *
 * Why not <picture>/<source>?
 * The production static file server returns images with
 * content-type: application/octet-stream (instead of image/webp).
 * Some browsers refuse to decode a <source type="image/webp"> when the
 * actual Content-Type mismatches, leading to broken/missing images.
 * A plain <img src="...webp"> works everywhere because browsers sniff
 * content bytes regardless of server Content-Type.
 *
 * IMAGE_VERSION is appended as a query string to bust browser/CDN caches
 * whenever we regenerate or correct dish images. Bump this when images change.
 */
const IMAGE_VERSION = 'v4';

const withVersion = (url) => {
  if (!url) return url;
  return url.includes('?') ? `${url}&_v=${IMAGE_VERSION}` : `${url}?_v=${IMAGE_VERSION}`;
};

const Fallback = ({ className, fallbackInitial, alt }) => (
  <div
    className={`bg-gradient-to-br from-[#F97316] via-[#FBBF24] to-[#2D4A22] flex items-center justify-center ${className}`}
  >
    <span className="font-['Playfair_Display'] text-7xl font-bold text-white/25 select-none">
      {fallbackInitial || (alt && alt[0]) || 'M'}
    </span>
  </div>
);

export const DishImage = ({
  src,
  alt,
  fallbackInitial,
  className = '',
  imgClassName = '',
  loading = 'lazy',
  fetchPriority,
}) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return <Fallback className={className} fallbackInitial={fallbackInitial} alt={alt} />;
  }

  return (
    <div className={className}>
      <img
        src={withVersion(src)}
        alt={alt}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        onError={() => setFailed(true)}
        className={imgClassName || 'w-full h-full object-cover'}
      />
    </div>
  );
};
